import React, { Dispatch, SetStateAction, useState } from "react";
import styled from "@emotion/styled";
import BackHeader from "@components/header/BackHeader";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import BasicButton from "@components/button/BasicButton";
import { useNavigate } from "react-router-dom";
import TossBillingButton from "@pages/subscribe/TossBillingButton";
import useUserInfoStore from "@store/useUserInfoStore";
import { useQuery } from "@tanstack/react-query";
import { useApiRequest } from "@utils/ApiRequest";
import { Flex } from "antd";

const Container = styled.div({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  alignItems: "space-between",
  backgroundColor: "#111111",
  height: "100dvh",
});

const TitleWrapper = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "2.4rem",
  alignItems: "space-between",
  padding: "6.8rem 2rem 2rem",
  backgroundColor: theme.color.white,

  ".date": {
    fontWeight: theme.fontWeight.bold,
    color: theme.color.light_gray,
  },

  ".start": {
    paddingTop: "9.2rem",
  },
}));

const TitleBox = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  fontSize: "2.4rem",
  lineHeight: "3.6rem",
  fontWeight: theme.fontWeight.bold,
  color: theme.color.black,

  ".point": {
    color: theme.color.green,
  },
}));

const ContentWrapper = styled.div(({ theme }) => ({
  // position: "fixed",
  // bottom: 0,
  width: "100%",
  maxWidth: "560px",
  heigth: "100%",
  display: "flex",
  flex: 1,
  flexDirection: "column",
  justifyContent: "space-between",
  gap: "14rem",
  color: theme.color.white,
  fontWeight: theme.fontWeight.regular,
  backgroundColor: theme.color.black,
  padding: "2.8rem 2rem 5.6rem",

  ".title": {
    fontSize: "2.4rem",
    fontWeight: theme.fontWeight.bold,
  },

  ".point": {
    fontSize: "1.6rem",
    color: theme.color.mint,
    fontWeight: theme.fontWeight.bold,
  },
}));

const Line = styled.div(({ theme }) => ({
  width: "100%",
  height: "1px",
  backgroundColor: theme.color.gray,
}));

const Desc = styled.ul(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  lineHeight: "2.4rem",
}));

const DescWrapper = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: "2rem",
});

const ButtonWrapper = styled.div(({ theme }) => ({
  width: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  gap: "2rem",
  lineHeight: "2rem",
  color: theme.color.gray,
}));

const HelpText = styled.span({
  textWrap: "wrap",
  // maxWidth: "26rem",
  overflowWrap: "break-word",
  textAlign: "center",
});

type Props = {
  tab: number;
  setTab: Dispatch<SetStateAction<number>>;
};
const SubscribeTabOne = ({ tab, setTab }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userInfo } = useUserInfoStore();

  const { getMemberSubscription } = useApiRequest();

  const { data: subscribeDetail } = useQuery({
    queryKey: ["subscribeDetail"],
    queryFn: () => getMemberSubscription(),
  });

  const handleNextTab = () => {
    if (subscribeDetail?.is_subscribed) {
      navigate("/subscribe-detail");
    }

    if (tab === 0) {
      setTab(1);
    }
  };

  return (
    <Container>
      <BackHeader title="구독" />

      <TitleWrapper>
        <TitleBox>
          {subscribeDetail?.is_subscribed ? (
            <>
              <span>
                <span>{t("현재 SHAKALAKA")}</span>
              </span>
              <Flex>
                <span className="point">{t("정기구독을 이용중")}</span>
                <span>{t("이십니다.")}</span>
              </Flex>
            </>
          ) : (
            <>
              <span>
                <span className="point">{t("멤버쉽 정기구독")}</span>
                <span>{t("을")}</span>
              </span>
              <span>{t("시작해보세요.")}</span>
            </>
          )}
        </TitleBox>

        {subscribeDetail?.is_subscribed ? (
          <div className="start">
            <strong>
              {userInfo?.nickname}
              {t("님")}
            </strong>
            <span>{t("은")} </span>
            <strong>{subscribeDetail?.start_date}</strong>
            <span>{t("에 구독을 시작하였습니다.")}</span>
          </div>
        ) : null}
      </TitleWrapper>

      <ContentWrapper>
        <DescWrapper>
          <span className="title">
            {t(`월 ${subscribeDetail?.amount?.toLocaleString()}원 결제`)}
          </span>
          <Line />
          <span className="point">{t("SHAKALAKA 구독 회원 혜택")}</span>

          <Desc>
            <li>
              {t(
                "• 유통마진 및 판매 수수료가 포함되지 않은 도매스틱 브랜드 세일상품(새상품) 무제한 도매가 쇼핑",
              )}
            </li>
            <li>
              {t(
                "• 세컨핸드 상품은 구독 여부과 상관없이, 셀러가 정한 판매가격 그대로 쇼핑이 가능합니다",
              )}
            </li>
            <li>{t("• 찜한 상품 정보 변동 알림")}</li>
            <li>{t("• 24/7 고객센터")}</li>
          </Desc>
        </DescWrapper>

        <ButtonWrapper>
          <HelpText>
            {t("* 계좌 입력에 대한 책임은 온전히 사용자에게 있습니다.")}
          </HelpText>

          {subscribeDetail?.is_subscribed ? (
            <BasicButton
              width="100%"
              title="구독내역 확인"
              height="5.2rem"
              point
              onClick={handleNextTab}
              big
            />
          ) : (
            <TossBillingButton title="월 5,500원 구독하기" />
          )}
        </ButtonWrapper>
      </ContentWrapper>
    </Container>
  );
};

export default SubscribeTabOne;
